import type { DriveItem } from "@/api/driveApi";
import type { DriveVariant } from "../types";

export type SortKey = "name" | "modified" | "size" | "opened" | "trashed" | "owner";

export interface SortState {
    key: SortKey;
    dir: "asc" | "desc";
}

const KEYS: SortKey[] = ["name", "modified", "size", "opened", "trashed", "owner"];

export function isSortState(v: unknown): v is SortState {
    if (!v || typeof v !== "object") return false;
    const s = v as { key?: unknown; dir?: unknown };
    return KEYS.includes(s.key as SortKey) && (s.dir === "asc" || s.dir === "desc");
}

export function defaultSort(variant: DriveVariant): SortState {
    if (variant === "recent") return { key: "opened", dir: "desc" };
    if (variant === "trash") return { key: "trashed", dir: "desc" };
    if (variant === "search" || variant === "shared") return { key: "modified", dir: "desc" };
    return { key: "name", dir: "asc" };
}

/** Direction a column starts in when first clicked (names/owners A→Z, dates/sizes biggest first). */
export function defaultDirFor(key: SortKey): "asc" | "desc" {
    return key === "name" || key === "owner" ? "asc" : "desc";
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const time = (iso: string | null | undefined): number => {
    if (!iso) return 0;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
};

/** Folders always first, then the chosen key; ties break by name. */
export function sortItems(list: DriveItem[], sort: SortState): DriveItem[] {
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === "Folder" ? -1 : 1;
        let cmp = 0;
        switch (sort.key) {
            case "name":
                cmp = collator.compare(a.name, b.name);
                break;
            case "size":
                // Folders have no meaningful size — keep them alphabetical.
                cmp = a.kind === "Folder" ? 0 : a.sizeBytes - b.sizeBytes;
                break;
            case "modified":
                cmp = time(a.updatedAt) - time(b.updatedAt);
                break;
            case "opened":
                cmp = time(a.lastAccessedAt) - time(b.lastAccessedAt);
                break;
            case "trashed":
                cmp = time(a.trashedAt) - time(b.trashedAt);
                break;
            case "owner":
                cmp = collator.compare(a.ownerEmail ?? "", b.ownerEmail ?? "");
                break;
        }
        if (cmp === 0) return collator.compare(a.name, b.name);
        return cmp * factor;
    });
}
